/**
 * 시민문제 폼의 **값**과 그 값 → API 본문 변환.
 *
 * **묻는 축의 정본은 FE(`haebom-ai-platform-fe`)다**(09-07 사용자 확정). 시민이 실제로 보는
 * 화면은 그쪽이고 여기는 관리자 도구가 같은 계약을 쓰는 자리다 — **여기에만 있는 칸은
 * 「시민이 답한 축」이 아니다.** 그걸 실물로 착각해 「폼에 칸이 있는데 안 채운다」로 읽은
 * 오진이 실제로 있었다(매칭 점수척도 진단 2차 개정).
 *
 * 그래서 **등록 축은 FE 등록 폼과 같다** — 원문·대상·주소·분야·연락처·동의뿐이다.
 * 축을 늘리려면 **FE부터 늘린다**(여기만 늘리면 다시 같은 오진이 난다).
 *
 * **수정은 다르다** — FE에 수정 화면이 아직 없고, 서버 `GET/PUT …/edit`은 등록 당시 값
 * 전부를 주고받는 계약이라 좁힐 기준이 없다. 그래서 수정 폼만 넓은 축을 유지한다.
 *
 * **수정은 부분 갱신이다**(`PUT …/edit` — 09-04에 전체 교체에서 뒤집었다). 안 보낸 축은
 * 서버가 종전 값을 지키고, **지우려면 `null`을 명시해야 한다.** 그래서 「비운 칸」을 보내는
 * 방식이 등록과 수정에서 다르다 — 등록은 `toProblemPayload`(FE 축·키 생략), 수정은
 * `toEditPayload`(넓은 축·`null`·`[]` 명시)로 함수 자체가 갈린다.
 */

import type { AddressPayload, EditableProblemCard } from './api'
import type { ConsentKey, ContactChannel } from './citizenContact'

/** API의 `UNKNOWNABLE_FIELDS`와 같은 값이어야 한다(`apps/api/src/lib/problem-fields.ts`). */
export const UNKNOWN_FIELD = {
  category: 'category_codes',
  // 022 — 코드 축(`needed_resource_codes`)과 서술 축(`needed_resource_note`)은 **다른 필드다.**
  // 키 이름을 겹치게 두면 체크박스 하나가 둘을 함께 끄는데, TS는 그걸 잡아 주지 않는다.
  neededResourceCodes: 'needed_resource_codes',
  impactScope: 'impact_scope',
  desiredPeriod: 'desired_period',
  barrier: 'barrier_note',
  neededResourceNote: 'needed_resource_note',
} as const

export type ImpactScope = 'personal' | 'neighbor' | 'dong_wide'
export type DesiredPeriod = 'immediate' | 'within_3m' | 'within_6m' | 'anytime'
export type Urgency = 'low' | 'mid' | 'high'

export const IMPACT_SCOPES: { value: ImpactScope; label: string }[] = [
  { value: 'personal', label: '나 한 사람' },
  { value: 'neighbor', label: '이웃 몇 사람' },
  { value: 'dong_wide', label: '동네 전체' },
]

export const DESIRED_PERIODS: { value: DesiredPeriod; label: string }[] = [
  { value: 'immediate', label: '지금 당장' },
  { value: 'within_3m', label: '3개월 안' },
  { value: 'within_6m', label: '6개월 안' },
  { value: 'anytime', label: '언제든' },
]

export const URGENCIES: { value: Urgency; label: string }[] = [
  { value: 'high', label: '시급' },
  { value: 'mid', label: '보통' },
  { value: 'low', label: '여유' },
]

/**
 * 원문 하한 — 서버 게이트가 30자 미만을 422 `too_short`로 되돌려보낸다. zod `min(5)`는
 * 형식 방지선이고 실제 통과 구간은 30~2000자다. 여기를 5로 두면 버튼이 열린 채 반려된다.
 */
export const RAW_TEXT_MIN = 30

/**
 * 주 대상 상한 — 서버 `UpdateBody.targetCodes`가 `.max(3)`이고 FE도 3이다. 관리자가 바꾸는
 * 설정값(`categoryMax`)이 아니라 스키마에 박힌 수라 여기도 상수로 둔다.
 */
export const TARGET_MAX = 3

/**
 * 시민이 고른 자리. **좌표는 지도에만 쓰고 본문에 싣지 않는다**(설계 원칙 7).
 * `regionCode`가 빈 채로 `matchedName`만 있으면 「우리 목록에 없는 동」 — 광명시 밖이다.
 */
export interface PickedAddress {
  text: string
  regionCode: string
  matchedName: string
  legalCode: string
  legalName: string
  postalCode: string
  lat: number | null
  lng: number | null
}

export const EMPTY_ADDRESS: PickedAddress = {
  text: '',
  regionCode: '',
  matchedName: '',
  legalCode: '',
  legalName: '',
  postalCode: '',
  lat: null,
  lng: null,
}

/** 폼 한 벌의 값. 빈 문자열은 "아직 안 고름"이다 — 캐스트로 좁히지 않는다. */
export interface ProblemFormValue {
  rawText: string
  targetCodes: string[]
  address: PickedAddress
  contactName: string
  contactChannel: ContactChannel
  contactPhone: string
  contactEmail: string
  consents: ConsentKey[]
  categoryCodes: string[]
  neededResourceCodes: string[]
  urgency: Urgency
  impactScope: '' | ImpactScope
  desiredPeriod: '' | DesiredPeriod
  barrierNote: string
  neededResourceNote: string
  unknown: string[]
}

export const EMPTY_FORM: ProblemFormValue = {
  rawText: '',
  targetCodes: [],
  address: EMPTY_ADDRESS,
  contactName: '',
  contactChannel: 'phone',
  contactPhone: '',
  contactEmail: '',
  consents: [],
  categoryCodes: [],
  neededResourceCodes: [],
  urgency: 'mid',
  impactScope: '',
  desiredPeriod: '',
  barrierNote: '',
  neededResourceNote: '',
  unknown: [],
}

function isImpactScope(v: string | null): v is ImpactScope {
  return v === 'personal' || v === 'neighbor' || v === 'dong_wide'
}

function isDesiredPeriod(v: string | null): v is DesiredPeriod {
  return v === 'immediate' || v === 'within_3m' || v === 'within_6m' || v === 'anytime'
}

function isUrgency(v: string): v is Urgency {
  return v === 'low' || v === 'mid' || v === 'high'
}

function isConsentKey(v: string): v is ConsentKey {
  return v === 'privacy' || v === 'disclose'
}

/**
 * 서버가 준 등록 당시 값 → 폼 값. **모르는 값은 넣지 않는다** — 서버가 나중에 열거를
 * 늘려도 화면이 유령 선택지를 켜 두지 않게 좁히기로 확인한다(캐스트 금지).
 *
 * 주소는 `address` 객체가 정본이고, 없으면 `addressText`+`regionCode`로 되살린다
 * (드롭다운 폴백으로 등록한 카드에는 객체가 없다). **좌표는 오지 않는다** — 등록 때
 * 보내지 않았으므로 수정 화면에서 지도 미리보기는 다시 고를 때 생긴다.
 */
export function formValueOf(card: EditableProblemCard): ProblemFormValue {
  const address: PickedAddress = {
    text: card.address?.text ?? card.addressText ?? '',
    regionCode: card.regionCode,
    matchedName: card.address?.adminName ?? '',
    legalCode: card.address?.legalCode ?? '',
    legalName: card.address?.legalName ?? '',
    postalCode: card.address?.postalCode ?? '',
    lat: null,
    lng: null,
  }
  return {
    rawText: card.rawText,
    targetCodes: card.targetCodes,
    address,
    contactName: card.contactName ?? '',
    // 적어 둔 쪽을 연다 — 휴대폰이 있으면 휴대폰, 없고 이메일만 있으면 이메일.
    contactChannel: card.contactPhone === null && card.contactEmail !== null ? 'email' : 'phone',
    contactPhone: card.contactPhone ?? '',
    contactEmail: card.contactEmail ?? '',
    consents: card.consents.filter(isConsentKey),
    categoryCodes: card.categoryCodes ?? [],
    neededResourceCodes: card.neededResourceCodes ?? [],
    urgency: isUrgency(card.urgency) ? card.urgency : 'mid',
    impactScope: isImpactScope(card.impactScope) ? card.impactScope : '',
    desiredPeriod: isDesiredPeriod(card.desiredPeriod) ? card.desiredPeriod : '',
    barrierNote: card.barrierNote ?? '',
    neededResourceNote: card.neededResourceNote ?? '',
    unknown: card.unknownFields,
  }
}

/** 필수 축이 다 찼나. 서버가 같은 규칙을 다시 본다 — 여기는 「보내기 전에 알려 주기」다. */
export function isSubmittable(v: ProblemFormValue, requiredConsents: readonly ConsentKey[]): boolean {
  const contactValue = v.contactChannel === 'phone' ? v.contactPhone : v.contactEmail
  return (
    v.rawText.trim().length >= RAW_TEXT_MIN &&
    v.targetCodes.length > 0 &&
    v.address.regionCode !== '' &&
    v.contactName.trim() !== '' &&
    contactValue.trim() !== '' &&
    // 개수를 숫자로 박지 않는다 — 동의 항목이 늘면 조건도 함께 늘어야 한다.
    requiredConsents.every((c) => v.consents.includes(c))
  )
}

/** 폼이 고른 주소 → 전송 객체. 검색을 못 써 동만 고른 경우 `text`가 비므로 동 이름을 싣는다. */
export function addressPayloadOf(a: PickedAddress): AddressPayload {
  return {
    text: a.text.trim() !== '' ? a.text.trim() : a.matchedName,
    adminCode: a.regionCode,
    adminName: a.matchedName,
    ...(a.legalCode !== '' ? { legalCode: a.legalCode } : {}),
    ...(a.legalName !== '' ? { legalName: a.legalName } : {}),
    ...(a.postalCode !== '' ? { postalCode: a.postalCode } : {}),
  }
}

/**
 * 등록 본문 — **FE 등록 폼이 보내는 축만** 싣는다(원문·대상·주소·분야·연락처·동의).
 *
 * 여기에 축을 더하면 gui 등록만 넓어져 「시민이 답한 값」의 분포를 잘못 읽게 된다 —
 * 늘리려면 FE부터다(위 머리말).
 *
 * 안 보낸 축은 서버가 알아서 둔다: `urgency`는 등록 스키마의 `.default('mid')`가 채우고,
 * 나머지 열거·서술 축은 비운 채로 남아 등록 후 AI 후처리가 채운다(`problem-post-process`).
 *
 * 연락처는 **고른 수단 하나만** 싣는다(둘 다 보내면 어디로 연락할지 모호해진다).
 * 빈 축은 키를 아예 뺀다 — 수정은 그러면 안 되므로 `toEditPayload`가 따로 있다.
 */
export function toProblemPayload(v: ProblemFormValue) {
  const contactKey = v.contactChannel === 'phone' ? 'contactPhone' : 'contactEmail'
  const contactValue = v.contactChannel === 'phone' ? v.contactPhone : v.contactEmail

  return {
    rawText: v.rawText.trim(),
    targetCodes: v.targetCodes,
    address: addressPayloadOf(v.address),
    contactName: v.contactName.trim(),
    consents: [...v.consents],
    // 스프레드로 넣어야 키 이름이 유니온으로 좁혀진다.
    ...{ [contactKey]: contactValue.trim() },
    ...(v.categoryCodes.length > 0 ? { categoryCodes: v.categoryCodes } : {}),
  }
}

/**
 * 수정 본문 — 등록 본문(FE 축)에 **수정 화면만 받는 넓은 축**을 얹고, 비운 칸을
 * **「비웠다」로 명시**해 덮는다.
 *
 * `PUT …/edit`은 부분 갱신이라 **키를 빼면 종전 값이 남는다.** 등록처럼 빈 축을 생략하면
 * 시민이 지운 값이·「잘 모르겠어요」로 되돌린 축이 저장되지 않는다. 그래서 이 축들을
 * **언제나 싣고**, 비었으면 `null`(코드 배열은 `[]`)을 넣는다.
 *
 * `urgency`도 여기서만 싣는다 — 수정 스키마는 **기본값을 두지 않아** 「안 보냄」과 「보통으로
 * 바꿈」이 갈린다(등록과 정반대다).
 *
 * **등록과 한 함수로 두지 않는 이유는 타입이다** — 모드로 갈리는 한 함수는 반환이 유니온이
 * 되어 `null`을 안 받는 등록 본문에 맞지 않는다(`exactOptionalPropertyTypes`).
 */
export function toEditPayload(v: ProblemFormValue) {
  const isUnknown = (field: string) => v.unknown.includes(field)
  const impactScope = isUnknown(UNKNOWN_FIELD.impactScope) ? '' : v.impactScope
  const desiredPeriod = isUnknown(UNKNOWN_FIELD.desiredPeriod) ? '' : v.desiredPeriod
  const barrierNote = isUnknown(UNKNOWN_FIELD.barrier) ? '' : v.barrierNote.trim()
  const neededResourceNote = isUnknown(UNKNOWN_FIELD.neededResourceNote)
    ? ''
    : v.neededResourceNote.trim()

  return {
    ...toProblemPayload(v),
    urgency: v.urgency,
    ...(v.unknown.length > 0 ? { unknownFields: v.unknown } : {}),
    categoryCodes: isUnknown(UNKNOWN_FIELD.category) ? [] : v.categoryCodes,
    neededResourceCodes: isUnknown(UNKNOWN_FIELD.neededResourceCodes)
      ? []
      : v.neededResourceCodes,
    impactScope: impactScope !== '' ? impactScope : null,
    desiredPeriod: desiredPeriod !== '' ? desiredPeriod : null,
    barrierNote: barrierNote !== '' ? barrierNote : null,
    neededResourceNote: neededResourceNote !== '' ? neededResourceNote : null,
  }
}
